// analytics.js
// Analytics tracking utility for FlowTutor
// Tracks user behavior, feature usage, and engagement metrics

import { supabase } from './supabaseClient';

// Generate unique session ID
const getSessionId = () => {
  let sessionId = sessionStorage.getItem('flowtutor_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem('flowtutor_session_id', sessionId);
  }
  return sessionId;
};

// Get user info
const getUserInfo = () => {
  try {
    const user = supabase.auth.getUser();
    return {
      user_id: user?.id || 'anonymous',
      user_email: user?.email || null
    };
  } catch (error) {
    return {
      user_id: 'anonymous',
      user_email: null
    };
  }
};

// Get device/browser info
const getDeviceInfo = () => {
  return {
    user_agent: navigator.userAgent,
    platform: navigator.platform,
    language: navigator.language,
    screen_width: window.screen.width,
    screen_height: window.screen.height,
    viewport_width: window.innerWidth,
    viewport_height: window.innerHeight
  };
};

/**
 * Track an analytics event
 * @param {string} eventType - Type of event (e.g., 'button_click', 'roadmap_generated')
 * @param {string} eventCategory - Category (e.g., 'feature_usage', 'navigation', 'engagement')
 * @param {object} eventData - Additional data about the event
 * @param {object} metadata - Extra metadata
 */
export const trackEvent = async (eventType, eventCategory, eventData = {}, metadata = {}) => {
  try {
    const userInfo = getUserInfo();
    const sessionId = getSessionId();

    const { error } = await supabase
      .from('analytics_events')
      .insert([{
        user_id: userInfo.user_id,
        user_email: userInfo.user_email,
        event_type: eventType,
        event_category: eventCategory,
        event_data: eventData,
        session_id: sessionId,
        metadata: {
          ...metadata,
          timestamp: new Date().toISOString(),
          url: window.location.href,
          path: window.location.pathname
        }
      }]);

    if (error) {
      console.error('Analytics tracking error:', error);
    }
  } catch (error) {
    // Silently fail - don't disrupt user experience
    console.error('Analytics error:', error);
  }
};

/**
 * Track feature usage
 * @param {string} featureName - Name of the feature
 * @param {object} additionalData - Additional data about feature usage
 */
export const trackFeatureUsage = (featureName, additionalData = {}) => {
  trackEvent(
    featureName,
    'feature_usage',
    additionalData
  );
};

/**
 * Track roadmap generation
 * @param {string} topic - Learning goal/topic
 * @param {string} category - Category of learning goal
 */
export const trackRoadmapGeneration = (topic, category = null) => {
  trackEvent(
    'roadmap_generated',
    'content_creation',
    {
      topic: topic,
      category: category,
      length: topic.length
    }
  );
};

/**
 * Track lesson interaction
 * @param {string} lessonTitle - Title of the lesson
 * @param {string} action - Action taken (viewed, completed, etc.)
 */
export const trackLessonInteraction = (lessonTitle, action) => {
  trackEvent(
    'lesson_interaction',
    'engagement',
    {
      lesson: lessonTitle,
      action: action
    }
  );
};

/**
 * Track quiz/test completion
 * @param {string} testType - Type of test (quiz, SAT, ACT, etc.)
 * @param {object} results - Test results
 */
export const trackTestCompletion = (testType, results) => {
  trackEvent(
    'test_completed',
    'assessment',
    {
      test_type: testType,
      score: results.score || results.percentage,
      total_questions: results.total,
      correct_answers: results.correct,
      ...results
    }
  );
};

/**
 * Track session start
 */
export const trackSessionStart = async () => {
  try {
    const userInfo = getUserInfo();
    const sessionId = getSessionId();
    const deviceInfo = getDeviceInfo();

    const { error } = await supabase
      .from('user_sessions')
      .insert([{
        session_id: sessionId,
        user_id: userInfo.user_id,
        user_email: userInfo.user_email,
        device_info: deviceInfo,
        browser_info: {
          user_agent: navigator.userAgent,
          language: navigator.language
        }
      }]);

    if (error && error.code !== '23505') { // Ignore duplicate key errors
      console.error('Session tracking error:', error);
    }

    trackEvent('session_started', 'session', { device_info: deviceInfo });
  } catch (error) {
    console.error('Session start tracking error:', error);
  }
};

/**
 * Track session end
 */
export const trackSessionEnd = async () => {
  try {
    const sessionId = getSessionId();

    // Calculate session duration from first event
    const { data: firstEvent } = await supabase
      .from('analytics_events')
      .select('created_at')
      .eq('session_id', sessionId)
      .order('created_at', { ascending: true })
      .limit(1)
      .single();

    if (firstEvent) {
      const durationSeconds = Math.floor(
        (new Date() - new Date(firstEvent.created_at)) / 1000
      );

      const { data: eventCount } = await supabase
        .from('analytics_events')
        .select('*', { count: 'exact', head: true })
        .eq('session_id', sessionId);

      await supabase
        .from('user_sessions')
        .update({
          ended_at: new Date().toISOString(),
          duration_seconds: durationSeconds,
          events_count: eventCount?.count || 0
        })
        .eq('session_id', sessionId);

      trackEvent('session_ended', 'session', { duration_seconds: durationSeconds });
    }
  } catch (error) {
    console.error('Session end tracking error:', error);
  }
};

/**
 * Track page view
 * @param {string} pageName - Name of the page/view
 */
export const trackPageView = (pageName) => {
  trackEvent(
    'page_view',
    'navigation',
    {
      page: pageName,
      referrer: document.referrer
    }
  );
};

/**
 * Track search/query
 * @param {string} query - Search query
 * @param {string} context - Context of search
 */
export const trackSearch = (query, context = 'general') => {
  trackEvent(
    'search',
    'interaction',
    {
      query: query,
      context: context,
      query_length: query.length
    }
  );
};

/**
 * Track button/action click
 * @param {string} buttonName - Name/identifier of the button
 * @param {string} context - Context where button was clicked
 */
export const trackClick = (buttonName, context = '') => {
  trackEvent(
    'button_click',
    'interaction',
    {
      button: buttonName,
      context: context
    }
  );
};

/**
 * Track error occurrence
 * @param {string} errorType - Type of error
 * @param {object} errorDetails - Error details
 */
export const trackError = (errorType, errorDetails = {}) => {
  trackEvent(
    'error_occurred',
    'error',
    {
      error_type: errorType,
      ...errorDetails
    }
  );
};

/**
 * Track time spent on feature
 * @param {string} featureName - Name of the feature
 * @param {number} durationSeconds - Time spent in seconds
 */
export const trackTimeSpent = (featureName, durationSeconds) => {
  trackEvent(
    'time_spent',
    'engagement',
    {
      feature: featureName,
      duration_seconds: durationSeconds,
      duration_minutes: Math.round(durationSeconds / 60)
    }
  );
};

// Initialize session tracking on page load
if (typeof window !== 'undefined') {
  // Track session start when module loads
  trackSessionStart();

  // Track session end on page unload
  window.addEventListener('beforeunload', () => {
    trackSessionEnd();
  });

  // Track when user becomes inactive (optional)
  let inactivityTimer;
  const resetInactivityTimer = () => {
    clearTimeout(inactivityTimer);
    inactivityTimer = setTimeout(() => {
      trackEvent('user_inactive', 'engagement', { inactive_duration: 300 });
    }, 5 * 60 * 1000); // 5 minutes
  };

  document.addEventListener('mousemove', resetInactivityTimer);
  document.addEventListener('keypress', resetInactivityTimer);
  document.addEventListener('click', resetInactivityTimer);
  document.addEventListener('scroll', resetInactivityTimer);

  resetInactivityTimer();
}

export default {
  trackEvent,
  trackFeatureUsage,
  trackRoadmapGeneration,
  trackLessonInteraction,
  trackTestCompletion,
  trackSessionStart,
  trackSessionEnd,
  trackPageView,
  trackSearch,
  trackClick,
  trackError,
  trackTimeSpent
};
